import { useEffect, useRef, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation, Trans } from 'react-i18next'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import { loadScript } from '../../../utils/scriptLoader.js'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import './Registration.css'
import dayjs from 'dayjs'

const ClosedRegistrationMessage = ({ status }) => {
  const { t } = useTranslation('race')
  return (
    <Box role='alert'>
      <Typography sx={{ mb: 2 }} variant='h6'>
        {status === 'soon' || status === 'later'
          ? t('registration.notOpen')
          : t('registration.closed')}
      </Typography>
      {(status === 'soon' || status === 'later') && <Typography>{t('registration.comeBackLater')}</Typography>}
    </Box>
  )
}

const initializeRaceRegistration = raceId => {
  return new Promise((resolve, reject) => {
    const container = document.getElementById('divRRRegStart')

    if (!container) {
      reject(new Error('Container not found'))
      return
    }

    if (container.dataset.initialized) {
      resolve(window.$RRRegStartRef)
      return
    }

    loadScript('/libs/mrr-registration.js', 'mrr')
      .then(() => {
        if (typeof window.RRRegStart !== 'function') {
          reject(new Error('RRRegStart is not available'))
          return
        }

        // The loader resolves once the ES module and stylesheet are ready.
        window.RRRegStart(container, raceId, 'registration', false)
          .then(rrp => {
            container.dataset.initialized = 'true'
            resolve(rrp)
          })
          .catch(error => {
            reject(new Error('RRRegStart initialization failed: ' + error.message))
          })
      })
      .catch(error => {
        reject(new Error('Script loading failed: ' + error.message))
      })
  })
}

export default function Registration ({ eventDetails }) {
  const registrationRef = useRef(null)
  const lastInitializedRaceId = useRef(null)
  const { myRaceResultsId, firstEventDate } = eventDetails || {}
  // Memoize time calculation to prevent recomputation on every render
  const earliestStartTime = useMemo(() => {
    return Object.values(eventDetails.startTimes || {}).reduce((earliest, time) => {
      const parts = time.match(/(\d+):(\d+) (\w+)/)
      let hour = parseInt(parts[1], 10)
      if (parts[3] === 'PM' && hour !== 12) hour += 12
      if (parts[3] === 'AM' && hour === 12) hour = 0

      const current = firstEventDate.clone()
        // .add(5, 'day')
        .hour(hour)
        .minute(parseInt(parts[2], 10))
      return !earliest || current.isBefore(earliest) ? current : earliest
    }, null)
  }, [eventDetails.startTimes, firstEventDate])

  const cutoffTime = useMemo(() =>
    earliestStartTime?.subtract(eventDetails.regCloseHoursBeforeStart || 12, 'hour'),
  [earliestStartTime, eventDetails.regCloseHoursBeforeStart]
  )

  const now = dayjs()

  // Memoize initialization function to prevent recreation on every render
  const initRegistration = useCallback(() => {
    if (myRaceResultsId && registrationRef.current) {
      initializeRaceRegistration(myRaceResultsId)
        .catch(error => console.error('Registration init failed:', error))
    }
  }, [myRaceResultsId])

  // Registration initialization effect
  useEffect(() => {
    if (myRaceResultsId !== lastInitializedRaceId.current &&
        eventDetails.status === 'open' &&
        !eventDetails.registrationsClosed &&
        (!cutoffTime || !now.isAfter(cutoffTime))) {
      lastInitializedRaceId.current = myRaceResultsId
      if (registrationRef.current) registrationRef.current.dataset.initialized = ''
      initRegistration()
    }
  }, [myRaceResultsId, eventDetails.status, eventDetails.registrationsClosed, cutoffTime, now, initRegistration])

  if (eventDetails.status !== 'open' || eventDetails.registrationsClosed || (cutoffTime && now.isAfter(cutoffTime))) {
    return (
      <Container maxWidth='xl' sx={{ overflow: 'auto' }} role='main'>
        <ClosedRegistrationMessage status={eventDetails.status} />
      </Container>
    )
  }

  return (
    <Container maxWidth='xl' sx={{ overflow: 'auto' }} role='main'>
      <Typography sx={{ mb: 2 }}>
        <Trans
          i18nKey='registration.embedNote'
          ns='race'
          components={{
            1: <Link
              href={`https://my.raceresult.com/${myRaceResultsId}/registration`}
              target='_blank'
              rel='noopener noreferrer'
              sx={{ textDecoration: 'underline' }}
               />
          }}
        />
      </Typography>
      <Box ref={registrationRef} id='divRRRegStart' className='RRRegStart' />
    </Container>
  )
}

Registration.propTypes = {
  eventDetails: PropTypes.shape({
    myRaceResultsId: PropTypes.string,
    firstEventDate: PropTypes.object,
    startTimes: PropTypes.object,
    regCloseHoursBeforeStart: PropTypes.number,
    status: PropTypes.oneOf(['open', 'soon', 'later']),
    registrationsClosed: PropTypes.bool
  })
}
